// src/components/AdminSettings/constants.ts
import type { SegmentOption } from '../ui/Segment';
import type { SelectOption } from '../ui/FormSelect';
import type { SettingsTab } from './types';
import type { ThemeMode } from '../../hooks/useTheme';
import type { RelationshipType } from '../../types';
import { TIMEZONE_OPTIONS, RELATIONSHIP_OPTIONS } from '../../types/user';

// Tab navigation options
export const TAB_OPTIONS: SegmentOption<SettingsTab>[] = [
  { value: 'family', label: 'Family' },
  { value: 'chores', label: 'Tasks' },
  { value: 'rewards', label: 'Rewards' },
  { value: 'photos', label: 'Photos' },
  { value: 'display', label: 'Display' },
  { value: 'appearance', label: 'Theme' },
  { value: 'integrations', label: 'Connect' },
  { value: 'account', label: 'Account' },
];

// Theme mode options for the appearance tab
export const THEME_OPTIONS: Array<{ mode: ThemeMode; label: string; icon: string; description: string }> = [
  { mode: 'light', label: 'Light', icon: '☀️', description: 'Always use light theme' },
  { mode: 'dark', label: 'Dark', icon: '🌙', description: 'Always use dark theme' },
  { mode: 'system', label: 'System', icon: '📱', description: 'Follow device settings' },
];

// Convert relationship options to FormSelect format
export const RELATIONSHIP_SELECT_OPTIONS: SelectOption<RelationshipType>[] = RELATIONSHIP_OPTIONS.map(r => ({
  id: r.value,
  label: r.label,
  value: r.value,
}));

// Convert timezone options to FormSelect format
export const TIMEZONE_SELECT_OPTIONS: SelectOption<string>[] = TIMEZONE_OPTIONS.map(t => ({
  id: t.value,
  label: t.label,
  value: t.value,
}));

// Frequency labels for chores
export const FREQUENCY_LABELS: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  as_needed: 'As Needed',
};

// Default form values
export const DEFAULT_PROFILE_FORM = {
  full_name: '',
  display_name: '',
  phone: '',
  timezone: 'America/Chicago',
};

export const DEFAULT_MEMBER_FORM = {
  name: '',
  nickname: '',
  relationship: '' as const,
  avatar: { type: 'icon' as const, value: '👤', iconType: 'emoji' as const },
  color: '#3b82f6', // Will be overridden by MEMBER_COLORS[0]
  is_parent: false,
  member_type: 'adult' as const,
  birth_date: null as string | null,
};

// Member type options for the dropdown
export const MEMBER_TYPE_OPTIONS = [
  { id: 'adult', label: 'Adult', value: 'adult' as const },
  { id: 'teen', label: 'Teen (13-17)', value: 'teen' as const },
  { id: 'child', label: 'Child (0-12)', value: 'child' as const },
  { id: 'pet', label: 'Pet', value: 'pet' as const },
];

export const DEFAULT_CHORE_FORM: import('./types').ChoreFormData = {
  name: '',
  icon: '🧹',
  points_value: 1,
  frequency: 'daily',
  schedule_categories: ['anytime'], // Array - tasks can belong to multiple routines
};

// Schedule category options for the dropdown
export const SCHEDULE_CATEGORY_OPTIONS = [
  { id: 'anytime', label: 'Any Time', value: 'anytime' as const },
  { id: 'morning', label: 'Morning Routine', value: 'morning' as const },
  { id: 'after_school', label: 'After School', value: 'after_school' as const },
  { id: 'evening', label: 'Evening', value: 'evening' as const },
  { id: 'bedtime', label: 'Bedtime', value: 'bedtime' as const },
  { id: 'weekend', label: 'Weekend', value: 'weekend' as const },
];

export const DEFAULT_REWARD_FORM = {
  name: '',
  icon: '🎁',
  points_cost: 10,
};
